import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, withModifiers as _withModifiers, normalizeClass as _normalizeClass, unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, createTextVNode as _createTextVNode, vModelText as _vModelText, withDirectives as _withDirectives, createElementBlock as _createElementBlock, renderList as _renderList, Fragment as _Fragment, normalizeStyle as _normalizeStyle, Teleport as _Teleport, createStaticVNode as _createStaticVNode } from "vue"

const _hoisted_1 = { class: "bg-white rounded-lg border border-gray-200 shadow-sm p-6 mb-8" }
const _hoisted_2 = { class: "grid grid-cols-1 lg:grid-cols-2 gap-8" }
const _hoisted_3 = { class: "space-y-4" }
const _hoisted_4 = { class: "text-black" }
const _hoisted_5 = { class: "flex gap-3" }
const _hoisted_6 = { class: "border-l border-gray-200 pl-8" }
const _hoisted_7 = { class: "flex items-center gap-2 sm:gap-3 px-3 sm:px-4 py-2.5 sm:py-3 border-b border-gray-200 flex-shrink-0" }
const _hoisted_8 = { class: "relative flex-1 max-w-md ml-auto min-w-0" }
const _hoisted_9 = { class: "px-4 py-2 border-b border-gray-100 text-xs text-black bg-gray-50 flex-shrink-0" }
const _hoisted_10 = { key: 0 }
const _hoisted_11 = { class: "flex-1 overflow-y-auto overscroll-contain p-4" }
const _hoisted_12 = {
  key: 0,
  class: "py-10 text-center text-sm text-gray-500"
}
const _hoisted_13 = {
  key: 1,
  class: "py-10 text-center text-sm text-red-600"
}
const _hoisted_14 = {
  key: 2,
  class: "py-10 text-center text-sm text-gray-500"
}
const _hoisted_15 = {
  key: 3,
  class: "space-y-5"
}
const _hoisted_16 = { class: "flex items-center gap-2 mb-2" }
const _hoisted_17 = { class: "text-sm font-semibold text-black" }
const _hoisted_18 = { class: "font-normal text-gray-500" }
const _hoisted_19 = { class: "grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2" }
const _hoisted_20 = ["title"]
const _hoisted_21 = { class: "border-t border-gray-200 px-4 py-3 flex justify-end flex-shrink-0" }

import { computed, ref } from "vue";
import { ActionButton } from "@/components";
import { getCouleursOfficielles, type CouleurAPILike } from "@/api/couleurs";
import {
  type BasicColorFamily,
  getColorSwatchStyle,
  getFamiliesWithVendorColors,
  groupVendorColorsByBasicFamily,
} from "@/utils/colorFamilies";

interface Props {
  selectedFile: File | null;
  isAnalyzing: boolean;
  isDownloadingTemplate?: boolean;
}

interface Emits {
  (e: "file-selected", file: File): void;
  (e: "analyze"): void;
  (e: "download-template"): void;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'MelExcelUploadZone',
  props: {
    selectedFile: {},
    isAnalyzing: { type: Boolean },
    isDownloadingTemplate: { type: Boolean, default: false }
  },
  emits: ["file-selected", "analyze", "download-template"],
  setup(__props: any, { emit: __emit }) {


const emit = __emit;

const fileInputRef = ref<HTMLInputElement | null>(null);
const isDragOver = ref(false);

const showOfficialColorsModal = ref(false);
const officialColors = ref<CouleurAPILike[]>([]);
const loadingColors = ref(false);
const loadError = ref<string | null>(null);
const searchTerm = ref("");

const normalize = (value: string) =>
  value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();

const filteredColors = computed(() => {
  const list = officialColors.value;
  if (!searchTerm.value) return list;
  const term = normalize(searchTerm.value);
  return list.filter(
    (c) =>
      normalize(c.couleur_FR || "").includes(term) ||
      normalize(c.couleur_EN || "").includes(term),
  );
});

const groups = computed(() =>
  groupVendorColorsByBasicFamily(filteredColors.value),
);

const visibleFamilies = computed(() =>
  getFamiliesWithVendorColors(filteredColors.value),
);

const familySwatchStyle = (family: BasicColorFamily) => {
  if (family.swatch.startsWith("linear-gradient")) {
    return { backgroundImage: family.swatch };
  }
  return { backgroundColor: family.swatch };
};

const loadOfficialColors = async () => {
  loadingColors.value = true;
  loadError.value = null;
  try {
    officialColors.value = await getCouleursOfficielles();
  } catch (err) {
    loadError.value =
      err instanceof Error
        ? err.message
        : "Impossible de charger les couleurs officielles";
    officialColors.value = [];
  } finally {
    loadingColors.value = false;
  }
};

const openOfficialColorsModal = async () => {
  showOfficialColorsModal.value = true;
  searchTerm.value = "";
  if (!officialColors.value.length && !loadingColors.value) {
    await loadOfficialColors();
  }
};

const triggerFileInput = () => {
  fileInputRef.value?.click();
};

const handleFileChange = (event: Event) => {
  const target = event.target as HTMLInputElement;
  if (target.files && target.files.length > 0) {
    const file = target.files[0];
    if (isValidExcelFile(file)) {
      emit("file-selected", file);
    }
  }
};

const handleDragOver = (event: DragEvent) => {
  event.preventDefault();
  isDragOver.value = true;
};

const handleDragLeave = (event: DragEvent) => {
  event.preventDefault();
  isDragOver.value = false;
};

const handleFileDrop = (event: DragEvent) => {
  event.preventDefault();
  isDragOver.value = false;

  const files = event.dataTransfer?.files;
  if (files && files.length > 0) {
    const file = files[0];
    if (isValidExcelFile(file)) {
      emit("file-selected", file);
    }
  }
};

const isValidExcelFile = (file: File): boolean => {
  const validExtensions = [".xls", ".xlsx"];
  const fileName = file.name.toLowerCase();
  return validExtensions.some((ext) => fileName.endsWith(ext));
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", null, [
        _cache[10] || (_cache[10] = _createElementVNode("h3", { class: "text-lg font-semibold text-black mb-4" }, " Upload de fichier Excel ", -1)),
        _cache[11] || (_cache[11] = _createElementVNode("p", { class: "text-sm text-black mb-4" }, " Choisir votre fichier Excel (.xls ou .xlsx) contenant toutes les données de vos produits ", -1)),
        _createElementVNode("div", _hoisted_3, [
          _createElementVNode("div", {
            class: _normalizeClass(["border-2 border-dashed rounded-lg p-6 text-center transition-colors cursor-pointer", 
              isDragOver.value
                ? 'border-blue-400 bg-blue-50'
                : 'border-gray-300 hover:border-blue-400'
            ]),
            onDragover: _withModifiers(handleDragOver, ["prevent"]),
            onDragleave: _withModifiers(handleDragLeave, ["prevent"]),
            onDrop: _withModifiers(handleFileDrop, ["prevent"]),
            onClick: triggerFileInput
          }, [
            _createElementVNode("input", {
              ref_key: "fileInputRef",
              ref: fileInputRef,
              type: "file",
              accept: ".xls,.xlsx",
              onChange: handleFileChange,
              class: "hidden"
            }, null, 544),
            _cache[9] || (_cache[9] = _createElementVNode("i", { class: "fas fa-file-excel text-4xl text-green-500 mb-2" }, null, -1)),
            _createElementVNode("p", _hoisted_4, _toDisplayString(_ctx.selectedFile
                  ? _ctx.selectedFile.name
                  : "Glissez-déposez un fichier Excel ou cliquez pour sélectionner"), 1)
          ], 34),
          _createElementVNode("div", _hoisted_5, [
            (_ctx.selectedFile)
              ? (_openBlock(), _createBlock(_unref(ActionButton), {
                  key: 0,
                  label: "Analyser le fichier",
                  icon: "fas fa-search",
                  variant: "secondary",
                  onClick: _cache[0] || (_cache[0] = ($event: any) => (_ctx.$emit('analyze'))),
                  loading: _ctx.isAnalyzing,
                  class: "flex-1"
                }, null, 8, ["loading"]))
              : _createCommentVNode("", true)
          ])
        ])
      ]),
      _createElementVNode("div", _hoisted_6, [
        _cache[15] || (_cache[15] = _createElementVNode("h3", { class: "text-lg font-semibold text-black mb-4" }, "Template Excel", -1)),
        _cache[16] || (_cache[16] = _createElementVNode("p", { class: "text-sm text-black mb-4" }, " Note : Vous pouvez télécharger ci-dessous le template Excel pour vos futures mises en ligne. ", -1)),
        _createVNode(_unref(ActionButton), {
          label: "Template Excel",
          icon: "fas fa-file-excel",
          variant: "success",
          loading: _ctx.isDownloadingTemplate,
          disabled: _ctx.isDownloadingTemplate,
          onClick: _cache[1] || (_cache[1] = ($event: any) => (_ctx.$emit('download-template'))),
          class: "w-full mb-4"
        }, null, 8, ["loading", "disabled"]),
        _cache[17] || (_cache[17] = _createStaticVNode("<div class=\"bg-sky-50 border border-sky-200 rounded-lg p-4 mb-3\"><h4 class=\"text-sm font-semibold text-sky-900 mb-2\"><i class=\"fas fa-ruler-combined mr-2\"></i>Colonne « Tailles » </h4><p class=\"text-xs text-sky-800 mb-2\"> Pour les ventes <strong>par couleurs</strong> ou <strong>mélangées</strong>, écrivez : </p><p class=\"text-sm text-sky-900 font-semibold mb-2\"><code class=\"bg-white px-2 py-1 rounded border border-sky-200\">1*S,1*M,1*L</code><span class=\"font-normal text-sky-700 text-xs ml-1\">et non</span><code class=\"bg-white px-2 py-1 rounded border border-sky-200 font-normal text-sky-600 line-through ml-1\">S,M,L</code></p><p class=\"text-xs text-sky-700\"> Une seule taille : <code class=\"bg-white px-1.5 py-0.5 rounded border border-sky-200\">1*TU</code> · Vendu par tailles : <code class=\"bg-white px-1.5 py-0.5 rounded border border-sky-200\">S,M,L</code></p></div>", 1)),
        _createElementVNode("div", { class: "bg-amber-50 border border-amber-200 rounded-lg p-4" }, [
          _cache[14] || (_cache[14] = _createElementVNode("h4", { class: "text-sm font-semibold text-amber-800 mb-2" }, [
            _createElementVNode("i", { class: "fas fa-exclamation-triangle mr-2" }),
            _createTextVNode("Avant d'importer ")
          ], -1)),
          _createElementVNode("ul", { class: "text-xs text-amber-700 space-y-1" }, [
            _createElementVNode("li", null, [
              _cache[12] || (_cache[12] = _createTextVNode(" • Utilisez uniquement les ")),
              _createElementVNode("button", {
                type: "button",
                class: "font-semibold underline underline-offset-2 hover:text-amber-900",
                onClick: openOfficialColorsModal
              }, " couleurs officielles disponibles ")
            ]),
            _cache[13] || (_cache[13] = _createStaticVNode("<li> • Vérifiez que vos <strong>tailles</strong> existent dans &quot;Mes déclinaisons&quot; </li><li> • Vérifiez que vos <strong>packs</strong> existent dans &quot;Gestion des paquets&quot; </li><li> • <strong>Compositions</strong> : format &quot;Coton*80,Polyester*20&quot; ou &quot;Coton*46.5,Polyester*53.5&quot; (total = 100%, décimales avec point) </li><li> • <strong>Chaussures</strong> : &quot;Tige*Cuir,Doublure*Textile,Semelle*Caoutchouc&quot; </li><li> • <strong>Références &quot;vendu par&quot; avec couleurs</strong> : si une même référence existe en plusieurs quantités de paquet, modifiez la référence pour les distinguer (ex : <strong>0000</strong> → <strong>0000-p1</strong>, <strong>0000-p6</strong>) </li><li> • <strong>Stock</strong> : format selon le type de vente — <em>par couleur</em> : <code>rouge*10,gris*5</code> | <em>couleurs mélangées</em> : <code>10</code> (quantité de paquets) | <em>par taille</em> : <code>rouge*S*5,rouge*M*10,gris*S*3,gris*M*8</code> (couleur*taille*quantité) </li>", 6))
          ])
        ])
      ])
    ]),
    (_openBlock(), _createBlock(_Teleport, { to: "body" }, [
      (showOfficialColorsModal.value)
        ? (_openBlock(), _createElementBlock("div", {
            key: 0,
            class: "fixed inset-0 z-[9999] overflow-hidden flex items-center justify-center p-3 md:p-4",
            onWheel: _cache[8] || (_cache[8] = _withModifiers(() => {}, ["prevent"]))
          }, [
            _createElementVNode("div", {
              class: "absolute inset-0 bg-black/40",
              onClick: _cache[2] || (_cache[2] = ($event: any) => (showOfficialColorsModal.value = false))
            }),
            _createElementVNode("div", {
              class: "relative w-full max-w-3xl bg-white rounded-2xl shadow-xl max-h-[85dvh] overflow-hidden flex flex-col",
              onClick: _cache[6] || (_cache[6] = _withModifiers(() => {}, ["stop"])),
              onWheel: _cache[7] || (_cache[7] = _withModifiers(() => {}, ["stop"]))
            }, [
              _createElementVNode("div", _hoisted_7, [
                _cache[20] || (_cache[20] = _createElementVNode("h3", { class: "text-base sm:text-lg font-semibold text-black whitespace-nowrap" }, " Couleurs officielles ", -1)),
                _createElementVNode("div", _hoisted_8, [
                  _cache[18] || (_cache[18] = _createElementVNode("i", { class: "fas fa-search absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs" }, null, -1)),
                  _withDirectives(_createElementVNode("input", {
                    "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((searchTerm).value = $event)),
                    type: "text",
                    autocomplete: "off",
                    placeholder: "Rechercher une couleur",
                    class: "w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-full focus:outline-none focus:ring-1 focus:ring-black focus:border-black"
                  }, null, 512), [
                    [
                      _vModelText,
                      searchTerm.value,
                      void 0,
                      { trim: true }
                    ]
                  ])
                ]),
                _createElementVNode("button", {
                  type: "button",
                  class: "text-gray-500 hover:text-black p-2 flex-shrink-0",
                  "aria-label": "Fermer",
                  onClick: _cache[4] || (_cache[4] = ($event: any) => (showOfficialColorsModal.value = false))
                }, _cache[19] || (_cache[19] = [
                  _createElementVNode("i", { class: "fas fa-times" }, null, -1)
                ]))
              ]),
              _createElementVNode("div", _hoisted_9, [
                _cache[21] || (_cache[21] = _createTextVNode(" Utilisez exactement ces libellés dans votre fichier Excel (colonne couleur). ")),
                (!loadingColors.value)
                  ? (_openBlock(), _createElementBlock("span", _hoisted_10, _toDisplayString(officialColors.value.length) + " couleur(s)", 1))
                  : _createCommentVNode("", true)
              ]),
              _createElementVNode("div", _hoisted_11, [
                (loadingColors.value)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_12, _cache[22] || (_cache[22] = [
                      _createElementVNode("i", { class: "fas fa-spinner fa-spin mr-2" }, null, -1),
                      _createTextVNode(" Chargement des couleurs… ")
                    ])))
                  : (loadError.value)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_13, [
                        _createTextVNode(_toDisplayString(loadError.value) + " ", 1),
                        _createElementVNode("button", {
                          type: "button",
                          class: "block mx-auto mt-2 underline",
                          onClick: loadOfficialColors
                        }, " Réessayer ")
                      ]))
                    : (visibleFamilies.value.length === 0)
                      ? (_openBlock(), _createElementBlock("div", _hoisted_14, " Aucune couleur trouvée "))
                      : (_openBlock(), _createElementBlock("div", _hoisted_15, [
                          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(visibleFamilies.value, (family) => {
                            return (_openBlock(), _createElementBlock("section", {
                              key: family.id
                            }, [
                              _createElementVNode("div", _hoisted_16, [
                                _createElementVNode("span", {
                                  class: "w-3.5 h-3.5 rounded-full flex-shrink-0 border border-black/10",
                                  style: _normalizeStyle(familySwatchStyle(family))
                                }, null, 4),
                                _createElementVNode("h4", _hoisted_17, [
                                  _createTextVNode(_toDisplayString(family.label) + " ", 1),
                                  _createElementVNode("span", _hoisted_18, " (" + _toDisplayString((groups.value[family.id] || []).length) + ") ", 1)
                                ])
                              ]),
                              _createElementVNode("div", _hoisted_19, [
                                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(groups.value[family.id] || [], (color) => {
                                  return (_openBlock(), _createElementBlock("div", {
                                    key: color.id_couleur,
                                    class: "flex items-center gap-2 px-2 py-1.5 rounded-md border border-gray-200 bg-white"
                                  }, [
                                    _createElementVNode("span", {
                                      class: "w-4 h-4 rounded-full flex-shrink-0 border border-black/15",
                                      style: _normalizeStyle(_unref(getColorSwatchStyle)(color.couleur_FR, color.hexColor))
                                    }, null, 4),
                                    _createElementVNode("span", {
                                      class: "text-xs text-black truncate",
                                      title: color.couleur_FR
                                    }, _toDisplayString(color.couleur_FR), 9, _hoisted_20)
                                  ]))
                                }), 128))
                              ])
                            ]))
                          }), 128))
                        ]))
              ]),
              _createElementVNode("div", _hoisted_21, [
                _createElementVNode("button", {
                  type: "button",
                  class: "px-4 py-2 text-sm bg-black text-white rounded-lg hover:bg-gray-800",
                  onClick: _cache[5] || (_cache[5] = ($event: any) => (showOfficialColorsModal.value = false))
                }, " Fermer ")
              ])
            ], 32)
          ], 32))
        : _createCommentVNode("", true)
    ]))
  ]))
}
}

})